import { FC, PropsWithChildren, useMemo } from "react";

import { SubScopeContext, SubScopeValue } from "./subScopeContext.lib";

/**
 * SubScopeProvider
 *
 * Wrap a subtree (e.g. a drawer or org-level shell) with this provider to
 * inject a workspaceId (or organizationId) that is not present in the main
 * ScopeContext. Every useScope() call inside the subtree will see the injected
 * values merged over the main scope — no consumer changes needed.
 *
 * Fallback behaviour (handled in useScope):
 *   - subScope.workspaceId set (incl. null) → wins over mainScope.workspaceId
 *     (an explicit null clears the workspace for the subtree)
 *   - subScope.workspaceId unset (undefined) → mainScope.workspaceId used as-is
 *
 * Usage:
 * ```tsx
 * <SubScopeProvider value={{ workspaceId: selectedClientWorkspaceId }}>
 *   <ServiceTemplateEditor />   // useScope() here returns workspaceId ✅
 * </SubScopeProvider>
 * ```
 */
export const SubScopeProvider: FC<
    PropsWithChildren<{ value: SubScopeValue }>
> = ({ value, children }) => {
    const memoizedValue = useMemo<SubScopeValue>(
        () => ({
            workspaceId: value.workspaceId,
        }),
        [value.workspaceId],
    );

    return (
        <SubScopeContext.Provider value={memoizedValue}>
            {children}
        </SubScopeContext.Provider>
    );
};
